import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { handleResponse } from "../utils/handleResponses";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import EndpointFunctions from "../utils/Functions/endpointFunctions";
import Endpoints from "../api/endpoints";

const CITIES_URL =
  "https://hemenpersonel-api.anemotech.com/common/location/getCities";
const DISTRICTS_URL =
  "https://hemenpersonel-api.anemotech.com/common/location/getDistricts";

const useCommonsService = () => {
  const axiosPrivate = useAxiosPrivate();

  const getCities = useQuery({
    queryKey: ["cities"],
    queryFn: async () => {
      const response = await axios.get(CITIES_URL, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return handleResponse({ response });
    },
    staleTime: Infinity, // Data stays fresh indefinitely, so it's only fetched once
    cacheTime: Infinity, // Keeps the data in cache forever, so it's never garbage-collected
    select: (data) => {
      return data?.data?.map((city) => ({
        label: city.name,
        value: city.id,
      }));
    },
  });

  const useDistricts = (cityId) => {
    return useQuery({
      queryKey: ["districts", cityId],
      queryFn: async () => {
        const response = await axios.get(`${DISTRICTS_URL}?cityId=${cityId}`, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
        return handleResponse({ response });
      },
      enabled: !!cityId, // Only fetch if cityId is provided
      staleTime: Infinity, // Prevent refetching for the same cityId
      cacheTime: Infinity, // Cache data indefinitely to avoid unnecessary fetches
      select: (data) => {
        return data?.data?.map((district) => ({
          label: district.name,
          value: district.id,
        }));
      },
    });
  };

  const useGetAllCountries = () => {
    return useQuery({
      queryKey: ["countries"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getCountries,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        return data?.data?.map((city) => ({
          label: city.name,
          value: city.id,
        }));
      },
    });
  };

  const useForeignLanguageLearnedPlatforms = () => {
    return useQuery({
      queryKey: ["foreignLanguageLearnedPlatform"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getForeignLanguageLearnedPlatforms,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useEducationTypes = () => {
    return useQuery({
      queryKey: ["educationTypes"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getEducationTypes,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useKnowledgeLevels = () => {
    return useQuery({
      queryKey: ["knowledgeLevels"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getKnowledgeLevels,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useYearsOfExperiences = () => {
    return useQuery({
      queryKey: ["yearsOfExperiences"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getYearsOfExperiences,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useSchoolTypes = () => {
    return useQuery({
      queryKey: ["schoolTypes"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getSchoolTypes,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useMaritalStatutes = () => {
    return useQuery({
      queryKey: ["maritalStatutes"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getMaritalStatutes,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGenders = () => {
    return useQuery({
      queryKey: ["genders"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getGenders,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useCompletionStatuses = () => {
    return useQuery({
      queryKey: ["completionStatuses"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getCompletionStatuses,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetAllEducationLanguages = () => {
    return useQuery({
      queryKey: ["educationLanguages"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllEducationLanguages,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.name;
          const value = item.id;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };
  const useGetAllForeignLanguages = () => {
    return useQuery({
      queryKey: ["foreignLanguages"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllForeignLanguages,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.name;
          const value = item.id;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetAllComputerInformations = () => {
    return useQuery({
      queryKey: ["computerInformation"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllComputerInformation,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.name;
          const value = item.id;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetAllSectors = () => {
    return useQuery({
      queryKey: ["sectors"],
      retry: 3,
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllSectors,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.name;
          const value = item.id;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetAllJobOptionCategories = () => {
    return useQuery({
      queryKey: ["jobOptionCategories"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllJobOptionCategories,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.name;
          const value = item.id;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetAllJobOptions = () => {
    return useQuery({
      queryKey: ["jobOptions"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllJobOptions,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        return data.data.map((category) => ({
          label: category.name,
          isRequired: category.isSelectionRequired,
          options: category.options.map((option) => ({
            label: option.name,
            value: option.id,
          })),
        }));
      },
    });
  };

  const useGetAllPositions = () => {
    return useQuery({
      queryKey: ["positions"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllPositions,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.name;
          const value = item.id;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetAllJobTypes = () => {
    return useQuery({
      queryKey: ["jobTypes"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllJobTypes,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetDurationTypes = () => {
    return useQuery({
      queryKey: ["durationTypes"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllDurationTypes,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetWageTypes = () => {
    return useQuery({
      queryKey: ["wageTypes"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllWageTypes,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetLabelKeys = () => {
    return useQuery({
      queryKey: ["labelKeys"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllLabelKeys,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetDepartments = () => {
    return useQuery({
      queryKey: ["departments"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllDepartments,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.name;
          const value = item.id;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetFirmRoles = () => {
    return useQuery({
      queryKey: ["firmRoles"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllFirmRoles,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  const useGetWagePeriods = () => {
    return useQuery({
      queryKey: ["wagePeriods"],
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async () => {
        const response = await EndpointFunctions.fetchData({
          endpointURL: Endpoints.getAllWagePeriods,
          axiosPrivate: axiosPrivate,
        });
        return handleResponse({ response });
      },
      select: (data) => {
        const options = data.data.map((item) => {
          const label = item.description;
          const value = item.name;
          return { label: label, value: value };
        });
        return options;
      },
    });
  };

  return {
    cities: getCities.data,
    isGetCitiesLoading: getCities.isLoading,
    useDistricts,
    useForeignLanguageLearnedPlatforms,
    useKnowledgeLevels,
    useEducationTypes,
    useCompletionStatuses,
    useGenders,
    useMaritalStatutes,
    useSchoolTypes,
    useYearsOfExperiences,
    useGetAllForeignLanguages,
    useGetAllEducationLanguages,
    useGetAllComputerInformations,
    useGetAllSectors,
    useGetAllPositions,
    useGetAllCountries,
    useGetAllJobOptionCategories,
    useGetAllJobOptions,
    useGetLabelKeys,
    useGetAllJobTypes,
    useGetDurationTypes,
    useGetWageTypes,
    useGetWagePeriods,
    useGetDepartments,
    useGetFirmRoles,
  };
};

export default useCommonsService;
